(() => {
  const instances = new WeakMap();

  function initialize(component) {
    if (instances.has(component)) return;
    const events = new AbortController();
    const on = (target, type, handler, options = {}) =>
      target.addEventListener(type, handler, { ...options, signal: events.signal });

    on(component, 'click', (event) => {
      const arrow = event.target.closest('.slider-float-arrow');
      if (arrow) {
        const direction = arrow.classList.contains('slider-float-arrow--prev') ? 'previous' : 'next';
        component.querySelector(`button[name="${direction}"]`)?.click();
        return;
      }
      if (event.target.closest('a, button, video[controls]')) return;
      const slide = event.target.closest('.slideshow__slide[data-slide-link]');
      if (slide && !dragged) window.location.href = slide.dataset.slideLink;
    });
    let startX = 0;
    let startY = 0;
    let dragged = false;
    on(component, 'touchstart', (event) => {
      startX = event.touches[0].clientX;
      startY = event.touches[0].clientY;
      dragged = false;
    }, { passive: true });
    on(component, 'touchmove', (event) => {
      dragged ||= Math.abs(event.touches[0].clientX - startX) > 15 ||
        Math.abs(event.touches[0].clientY - startY) > 15;
    }, { passive: true });

    const videos = [...component.querySelectorAll('.slideshow__video')];
    if (!videos.length) {
      instances.set(component, () => events.abort());
      return;
    }
    const slides = [...component.querySelectorAll('.slideshow__slide')];
    const automatic = component.slider.dataset.autoplay === 'true' && slides.length > 1;
    const originalPlay = component.play.bind(component);
    const originalPause = component.pause.bind(component);
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const failed = new WeakSet();
    let paused = false;
    let visible = false;
    let disposed = false;
    let playbackAttempt = 0;
    const activeVideo = () => slides[(component.currentPage || 1) - 1]?.querySelector('.slideshow__video');
    const allowed = () => !disposed && !paused && visible && !document.hidden && !motion.matches;
    const hydrate = (video) => {
      const template = video.querySelector('.slideshow__video-sources');
      if (!template) return;
      video.append(template.content);
      template.remove();
      video.load();
    };
    const stop = () => {
      playbackAttempt += 1;
      originalPause();
      videos.forEach((video) => video.pause());
    };
    const resume = () => {
      stop();
      if (!allowed()) return;
      const video = activeVideo();
      if (!video || failed.has(video)) {
        if (automatic) originalPlay();
        return;
      }
      if (video.ended) {
        if (automatic) component.autoRotateSlides();
        return;
      }
      hydrate(video);
      video.muted = true;
      const attempt = playbackAttempt;
      video.play().catch((error) => {
        if (attempt !== playbackAttempt || error.name === 'AbortError' || disposed || activeVideo() !== video || !allowed()) return;
        failed.add(video);
        if (automatic) originalPlay();
      });
    };
    // Existing hover, focus and autoplay controls call these methods dynamically.
    component.play = () => { paused = false; resume(); };
    component.pause = () => { paused = true; stop(); };
    on(component, 'slideChanged', () => {
      videos.forEach((video) => {
        if (video !== activeVideo()) {
          video.pause();
          if (video.readyState > 0) video.currentTime = 0;
          video.closest('.slideshow__media').classList.remove('slideshow__media--playing');
        }
      });
      resume();
    });
    videos.forEach((video) => {
      const template = video.querySelector('.slideshow__video-sources');
      const sources = [...(template ? template.content : video).querySelectorAll('source')];
      const failedSources = new Set();
      const failVideo = () => {
        failed.add(video);
        video.closest('.slideshow__media').classList.remove('slideshow__media--playing');
        if (activeVideo() === video) resume();
      };
      on(video, 'playing', () => {
        if (activeVideo() === video && allowed()) {
          video.closest('.slideshow__media').classList.add('slideshow__media--playing');
        }
      });
      on(video, 'ended', () => {
        if (activeVideo() === video && allowed() && automatic) component.autoRotateSlides();
      });
      on(video, 'error', failVideo);
      sources.forEach((source) => on(source, 'error', () => {
        failedSources.add(source);
        if (failedSources.size === sources.length) failVideo();
      }));
      if (!template && (video.error || video.networkState === HTMLMediaElement.NETWORK_NO_SOURCE)) failed.add(video);
    });
    on(document, 'visibilitychange', resume);
    on(motion, 'change', resume);
    const observer = new IntersectionObserver(([entry]) => {
      if (visible === entry.isIntersecting) return;
      visible = entry.isIntersecting;
      if (!visible) stop();
      else requestAnimationFrame(() => requestAnimationFrame(resume));
    }, { threshold: 0.01 });
    observer.observe(component);
    instances.set(component, () => {
      disposed = true;
      stop();
      observer.disconnect();
      events.abort();
    });
    resume();
  }

  const mount = (root) => root.querySelectorAll('slideshow-component').forEach(initialize);
  customElements.whenDefined('slideshow-component').then(() => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => mount(document), { once: true });
    } else mount(document);
    document.addEventListener('shopify:section:load', (event) => mount(event.target));
    document.addEventListener('shopify:section:unload', (event) => {
      event.target.querySelectorAll('slideshow-component').forEach((component) => {
        instances.get(component)?.();
        instances.delete(component);
      });
    });
  });
})();
